import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Refresh, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fetchSalaryStructures, createSalaryStructure, updateSalaryStructure } from '../../../../services/salary';
import { fetchEmployees, fetchPositions } from '../../../../services/employee';
import CustomPagination from '../../../../components/common/CustomPagination.js';

const SalaryStructures = () => {
  const { t } = useTranslation(['common', 'employees', 'salary']);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [structures, setStructures] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    employee: '',
    is_active: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 20,
    totalCount: 0
  });
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    employee: '',
    position: '',
    base_salary: '',
    salary_type: 'monthly',
    currency: 'NGN',
    effective_date: '',
    end_date: '',
    is_active: true,
    notes: ''
  });
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = Math.ceil(pagination.totalCount / pagination.rowsPerPage) || 1;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...filters,
        page: pagination.page + 1,
        page_size: pagination.rowsPerPage
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await fetchSalaryStructures(params);
      
      if (response.results) {
        setStructures(response.results);
        setPagination(prev => ({
          ...prev,
          totalCount: response.count || 0
        }));
      } else {
        setStructures(Array.isArray(response) ? response : []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch salary structures');
      setStructures([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      if (modalOpen) {
        try {
          const [employeesRes, positionsRes] = await Promise.all([
            fetchEmployees({ page_size: 1000 }).catch(() => ({ results: [] })),
            fetchPositions({ page_size: 1000 }).catch(() => ({ results: [] }))
          ]);
          
          setEmployees(employeesRes.results || employeesRes || []);
          setPositions(positionsRes.results || positionsRes || []);
        } catch (err) {
          console.error('Failed to fetch dropdown data:', err);
        }
      }
    };
    
    fetchDropdownData();
  }, [modalOpen]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setPagination(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
  };

  const handleCustomRowsInputChange = (value) => {
    setCustomRowsInput(value);
  };

  const handleCustomRowsInputBlur = () => {
    const value = parseInt(customRowsInput);
    if (value && value > 0 && value <= 1000) {
      setPagination(prev => ({ ...prev, rowsPerPage: value, page: 0 }));
      setCustomRowsActive(false);
      setCustomRowsInput('');
    } else {
      setCustomRowsInput('');
      setCustomRowsActive(false);
    }
  };

  const handleOpenModal = (structure = null) => {
    if (structure) {
      setEditId(structure.id);
      setForm({
        employee: structure.employee?.id || structure.employee || '',
        position: structure.position?.id || structure.position || '',
        base_salary: structure.base_salary || '',
        salary_type: structure.salary_type || 'monthly',
        currency: structure.currency || 'NGN',
        effective_date: structure.effective_date || '',
        end_date: structure.end_date || '',
        is_active: structure.is_active !== undefined ? structure.is_active : true,
        notes: structure.notes || ''
      });
    } else {
      setEditId(null);
      setForm({
        employee: '',
        position: '',
        base_salary: '',
        salary_type: 'monthly',
        currency: 'NGN',
        effective_date: '',
        end_date: '',
        is_active: true,
        notes: ''
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm({
      employee: '',
      position: '',
      base_salary: '',
      salary_type: 'monthly',
      currency: 'NGN',
      effective_date: '',
      end_date: '',
      is_active: true,
      notes: ''
    });
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.employee) {
      setSnackbar({ open: true, message: 'Employee is required', severity: 'error' });
      return;
    }

    if (!form.base_salary || parseFloat(form.base_salary) <= 0) {
      setSnackbar({ open: true, message: 'Base salary is required and must be greater than 0', severity: 'error' });
      return;
    }

    if (!form.effective_date) {
      setSnackbar({ open: true, message: 'Effective date is required', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        employee: form.employee,
        position: form.position || null,
        base_salary: form.base_salary,
        salary_type: form.salary_type,
        currency: form.currency,
        effective_date: form.effective_date,
        end_date: form.end_date || null,
        is_active: form.is_active,
        notes: form.notes || null
      };

      if (editId) {
        await updateSalaryStructure(editId, payload);
        setSnackbar({ open: true, message: 'Salary structure updated successfully', severity: 'success' });
      } else {
        await createSalaryStructure(payload);
        setSnackbar({ open: true, message: 'Salary structure created successfully', severity: 'success' });
      }
      
      handleCloseModal();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          Object.values(err.response?.data || {}).flat().join(', ') ||
                          err.message || 
                          'Failed to save salary structure';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const rowsWithSerial = useMemo(() => {
    return structures.map((item, index) => ({
      ...item,
      sn: pagination.page * pagination.rowsPerPage + index + 1
    }));
  }, [structures, pagination.page, pagination.rowsPerPage]);

  const columns = useMemo(() => [
    {
      field: 'sn',
      headerName: t('salary:structures.columns.sn'),
      width: 80,
      sortable: false,
      filterable: false
    },
    {
      field: 'employee_name',
      headerName: t('salary:structures.columns.employee'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'employee_id',
      headerName: t('salary:structures.columns.employeeId'),
      flex: 1,
      minWidth: 120,
      sortable: false
    },
    {
      field: 'position_title',
      headerName: t('salary:structures.columns.position'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => params.value || 'N/A'
    },
    {
      field: 'base_salary',
      headerName: t('salary:structures.columns.baseSalary'),
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const currency = params.row.currency || 'NGN';
        return `${currency} ${parseFloat(params.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      field: 'salary_type',
      headerName: t('salary:structures.columns.type'),
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const type = params.value || 'monthly';
        return type === 'monthly' ? t('salary:common.monthly') : t('salary:common.annual');
      }
    },
    {
      field: 'currency',
      headerName: t('salary:structures.columns.currency'),
      width: 100,
      sortable: false
    },
    {
      field: 'effective_date',
      headerName: t('salary:structures.columns.effectiveDate'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        try {
          return format(new Date(params.value), 'MMM dd, yyyy');
        } catch {
          return 'N/A';
        }
      }
    },
    {
      field: 'is_active',
      headerName: t('salary:structures.columns.status'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('salary:common.active') : t('salary:common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ], [t]);

  if (loading && structures.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('salary:structures.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenModal()}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('salary:structures.addStructure')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Search"
                fullWidth
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder={t('salary:structures.searchPlaceholder')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Employee"
                fullWidth
                value={filters.employee}
                onChange={(e) => handleFilterChange('employee', e.target.value)}
                placeholder="Employee ID"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Status"
                fullWidth
                value={filters.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">{t('salary:common.all')}</option>
                <option value="true">{t('salary:common.active')}</option>
                <option value="false">{t('salary:common.inactive')}</option>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520
          }}>
            <DataGrid
              rows={rowsWithSerial}
              columns={columns}
              disableSelectionOnClick
              loading={loading}
              hideFooterPagination
              hideFooter
              getRowSpacing={() => ({ top: 2, bottom: 2 })}
              sx={{
                border: 'none',
                minWidth: 1200,
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                  color: 'white'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem'
                },
                '& .MuiDataGrid-row': {
                  marginBottom: '4px',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={pagination.page}
          totalPages={totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>

      {/* Add/Edit Salary Structure Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? t('common:buttons.edit') + ' ' + t('salary:structures.title') : t('salary:structures.addStructure')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.employee_id || ''})`}
                  value={employees.find(e => e.id === form.employee) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('employee', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee"
                      required
                      placeholder="Select employee"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={positions}
                  getOptionLabel={(option) => `${option.title || ''} (${option.code || ''})`}
                  value={positions.find(p => p.id === form.position) || null}
                  onChange={(event, newValue) => {
                    handleFormChange('position', newValue?.id || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Position"
                      placeholder="Select position (optional)"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Base Salary"
                  type="number"
                  value={form.base_salary}
                  onChange={(e) => handleFormChange('base_salary', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Salary Type"
                  value={form.salary_type}
                  onChange={(e) => handleFormChange('salary_type', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="monthly">{t('salary:common.monthly')}</option>
                  <option value="annual">{t('salary:common.annual')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Currency"
                  value={form.currency}
                  onChange={(e) => handleFormChange('currency', e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="NGN">NGN - Nigerian Naira</option>
                  <option value="CNY">CNY - Chinese Yuan</option>
                  <option value="USD">USD - US Dollar</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Effective Date"
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => handleFormChange('effective_date', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => handleFormChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={form.is_active ? 'true' : 'false'}
                  onChange={(e) => handleFormChange('is_active', e.target.value === 'true')}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="true">{t('salary:common.active')}</option>
                  <option value="false">{t('salary:common.inactive')}</option>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={form.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  multiline
                  rows={3}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            {t('common:buttons.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={saving}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {saving ? <CircularProgress size={20} /> : (editId ? t('common:buttons.update') : t('common:buttons.create'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalaryStructures;

